import React, { FC, useState, useEffect, useCallback } from 'react';
import Papa from 'papaparse';
import { Api } from '@blink/components';
import { useLocation } from 'react-router-dom';

import { PATHS } from 'src/routes/paths';

import { SegmentButtonGroup } from '@/widgets/SegmentButtonGroup';
import { Faqs } from '@/widgets/FAQs';
import { OrderBookTable } from '@/widgets/OrderBookTable';

export type Data = { [key: string]: string };

export enum Mode {
    LEND = 'Lend',
    BORROW = 'Borrow',
}

export const groupList = [
    { text: Mode.LEND, value: PATHS.ORDER_BOOK, url: Api.LEND_BOOK_URL },
    { text: Mode.BORROW, value: PATHS.ORDER_BOOK_BORROW, url: Api.BORROW_BOOK_URL },
];

export const OrderBook: FC = () => {
    const location = useLocation();
    const referrer = location.state?.referrer || PATHS.HOME;

    const getDefaultGroup = () => {
        const defaultPath = referrer === '/borrow' ? PATHS.ORDER_BOOK_BORROW : PATHS.ORDER_BOOK;
        return groupList.find((group) => group.value === defaultPath) || groupList[0];
    };

    const [data, setData] = useState<Data[]>([]);
    const [activeGroup, setActiveGroup] = useState(getDefaultGroup);

    const handleFileUpload = useCallback(
        async (url: string) => {
            try {
                const response = await fetch(url);
                if (!response.ok) throw new Error(`File loading error: ${response.statusText}`);

                const csvText = await response.text();

                Papa.parse<Data>(csvText, {
                    header: true,
                    skipEmptyLines: true,
                    complete: ({ data: parsedData }) => {
                        const mappedData = parsedData.map((row) => ({
                            col1: row[activeGroup.text] || '',
                            col2: activeGroup.text === Mode.BORROW ? row['Collateral'] || '' : '',
                            col3: row['Amount(dollar notional)'] || '',
                            col4: row['APY']?.replace(',', '.') || '',
                            col5:
                                activeGroup.text === Mode.LEND
                                    ? row['Comment/Terms'] || ''
                                    : row['Usecase/LTV'] || '',
                        }));
                        setData(mappedData);
                    },
                    error: console.error,
                });
            } catch (error) {
                console.error('File loading error:', error);
            }
        },
        [activeGroup],
    );

    useEffect(() => {
        handleFileUpload(activeGroup.url);
    }, [activeGroup, handleFileUpload]);

    const handleGroupChange = (groupValue: string | number) => {
        const selectedGroup = groupList.find((group) => group.value === groupValue);
        if (selectedGroup) setActiveGroup(selectedGroup);
    };

    const pageTitle =
        activeGroup.text === Mode.BORROW
            ? 'Available Borrowing Opportunities'
            : 'Available Lending Opportunities';

    return (
        <>
            <SegmentButtonGroup
                list={groupList}
                handleSegmentChanged={handleGroupChange}
                initialValue={activeGroup.value}
            />
            <div className='w-full text-content-primary mt-[32px] px-[10%]'>
                <div className='flex flex-col justify-center items-start gap-[4px] px-5 pb-5 self-stretch'>
                    <div className='text-content-primary text-[16px] leading-[24px] font-semibold tracking-[-0.48px] font-inter'>
                        {pageTitle}
                    </div>
                </div>

                <div className='overflow-x-auto'>
                    <OrderBookTable data={data} label={activeGroup.text} />
                </div>
            </div>
            <Faqs />
        </>
    );
};
